import { oc } from '@orpc/contract'
import { z } from 'zod'
import {
  inboxEmailSchema,
  inboxEmailViewSchema,
  inboxFolderSchema,
  inboxRuleInputSchema,
  inboxRuleSchema,
  inboxRunSchema,
} from '../inbox'

/** Project inbox (PLAN-084). */

const projectInput = z.object({ projectId: z.string().min(1) })
const emailInput = projectInput.extend({ emailId: z.string().min(1) })

/**
 * The project's address plus a contact card for it: `vcard` is the full
 * vCard 3.0 text (the QR in the inbox header encodes it verbatim, so a phone
 * camera adds the project as a contact offline) and `downloadPath` is the
 * same text served as a `.vcf` for desktop browsers.
 */
export const inboxGetAddressContract = oc.input(projectInput).output(
  z.object({
    address: z.string(),
    contact: z.object({
      /** Display name of the contact — always prefixed "HUB · ". */
      name: z.string(),
      vcard: z.string(),
      downloadPath: z.string(),
    }),
  })
)

export const inboxListEmailsContract = oc
  .input(
    projectInput.extend({
      folder: inboxFolderSchema.default('inbox'),
      query: z.string().max(200).optional(),
      limit: z.number().int().min(1).max(500).optional(),
    })
  )
  .output(
    z.object({
      items: z.array(inboxEmailSchema),
      counts: z.object({ inbox: z.number(), processed: z.number(), archive: z.number() }),
      attention: z.number(),
    })
  )

export const inboxBadgeCountContract = oc
  .input(projectInput)
  .output(z.object({ count: z.number() }))

export const inboxGetEmailContract = oc.input(emailInput).output(
  z.object({
    email: inboxEmailViewSchema,
    runs: z.array(inboxRunSchema),
    rules: z.array(
      inboxRuleSchema.pick({ id: true, name: true, enabled: true, autoArchive: true })
    ),
  })
)

export const inboxMoveEmailContract = oc
  .input(emailInput.extend({ folder: inboxFolderSchema }))
  .output(z.object({ email: inboxEmailSchema }))

/**
 * Hard delete of an email (board 63qj): the row, its runs, its HUB thread and
 * the raw MIME (attachments) are gone for good. Refused with CONFLICT while
 * a run is processing it. Same permission as moveEmail.
 */
export const inboxDeleteEmailContract = oc
  .input(emailInput)
  .output(z.object({ success: z.literal(true) }))

export const inboxRunRuleContract = oc
  .input(emailInput.extend({ ruleId: z.string().min(1) }))
  .output(z.object({ run: inboxRunSchema }))

export const inboxAskAgentContract = oc
  .input(emailInput.extend({ prompt: z.string().trim().min(1).max(4000) }))
  .output(z.object({ run: inboxRunSchema }))

export const inboxReplyContract = oc
  .input(emailInput.extend({ text: z.string().trim().min(1).max(20_000) }))
  .output(z.object({ success: z.literal(true) }))

export const inboxSaveAttachmentContract = oc
  .input(emailInput.extend({ attachmentIndex: z.number().int().nonnegative() }))
  .output(z.object({ documentId: z.string(), title: z.string(), alreadySaved: z.boolean() }))

export const inboxListRulesContract = oc
  .input(projectInput)
  .output(z.object({ rules: z.array(inboxRuleSchema) }))

export const inboxCreateRuleContract = oc
  .input(projectInput.extend({ rule: inboxRuleInputSchema }))
  .output(z.object({ rule: inboxRuleSchema }))

export const inboxUpdateRuleContract = oc
  .input(projectInput.extend({ ruleId: z.string().min(1), rule: inboxRuleInputSchema.partial() }))
  .output(z.object({ rule: inboxRuleSchema }))

export const inboxDeleteRuleContract = oc
  .input(projectInput.extend({ ruleId: z.string().min(1) }))
  .output(z.object({ success: z.literal(true) }))

export const inboxReorderRulesContract = oc
  .input(projectInput.extend({ ruleIds: z.array(z.string().min(1)).max(200) }))
  .output(z.object({ rules: z.array(inboxRuleSchema) }))

/** Which recent emails a (draft) rule would match — the editor's preview. */
export const inboxTestRuleContract = oc
  .input(
    projectInput.extend({ rule: inboxRuleInputSchema.pick({ conditions: true, matchMode: true }) })
  )
  .output(
    z.object({
      matches: z.array(
        z.object({
          id: z.string(),
          subject: z.string().nullable(),
          fromAddress: z.string(),
          fromName: z.string().nullable(),
        })
      ),
      total: z.number(),
    })
  )
